/** Shared character part catalog, remaps, and frame constants. */

export const FRAME_WIDTH = 16;
export const FRAME_HEIGHT = 24;
export const WALK_FRAMES = 4;
export const DIRECTIONS = ['down', 'up', 'left', 'right'];
export const SHEET_WIDTH = FRAME_WIDTH * WALK_FRAMES;
export const SHEET_HEIGHT = FRAME_HEIGHT * DIRECTIONS.length;

/**
 * Walk cadence derived from movement speed so feet visually plant instead of
 * sliding: one 4-frame cycle covers two strides. Player at 4.375 tiles/s runs
 * ~12 fps; a strolling NPC at 2 tiles/s runs ~5.6 fps.
 */
export const FRAMES_PER_TILE = 2.8;

export function walkFrame(walkTime, speed) {
  return Math.floor(walkTime * speed * FRAMES_PER_TILE) % WALK_FRAMES;
}

export const NAME_MAX_LENGTH = 20;
export const LAST_APPEARANCE_KEY = 'worldcanvas.playerAppearance.v1';

/** Canonical 3-step ramps used while authoring parts (dark, mid, light). */
export const SKIN_AUTHOR = [5, 6, 10];
export const HAIR_AUTHOR = [3, 7, 8];
export const CLOTH_AUTHOR = [4, 28, 6];

export const GENDERS = [
  { id: 'male', name: 'Male' },
  { id: 'female', name: 'Female' },
];

export const SKIN_TONES = [
  { id: 'fair', name: 'Fair', ramp: [9, 10, 11] },
  { id: 'tan', name: 'Tan', ramp: [6, 8, 10] },
  { id: 'medium', name: 'Medium', ramp: [5, 7, 9] },
  { id: 'deep', name: 'Deep', ramp: [3, 5, 7] },
];

export const HAIR_STYLES = [
  { id: 'short', name: 'Short', hasBack: false },
  { id: 'medium', name: 'Medium', hasBack: false },
  { id: 'long', name: 'Long', hasBack: true },
];

export const HAIR_COLORS = [
  { id: 'black', name: 'Black', ramp: [1, 2, 24] },
  { id: 'brown', name: 'Brown', ramp: [3, 7, 8] },
  { id: 'blond', name: 'Blond', ramp: [8, 9, 10] },
  { id: 'auburn', name: 'Auburn', ramp: [4, 5, 28] },
  { id: 'gray', name: 'Gray', ramp: [24, 25, 27] },
];

export const TOPS = [
  { id: 'sleeveless', name: 'Sleeveless' },
  { id: 'shirt', name: 'Shirt' },
  { id: 'hoodie', name: 'Hoodie' },
];

export const BOTTOMS = [
  { id: 'pants', name: 'Pants' },
  { id: 'shorts', name: 'Shorts' },
  { id: 'skirt', name: 'Skirt' },
];

export const CLOTH_COLORS = [
  { id: 'red', name: 'Red', ramp: [4, 28, 6] },
  { id: 'blue', name: 'Blue', ramp: [24, 29, 27] },
  { id: 'berry', name: 'Berry', ramp: [2, 30, 6] },
  { id: 'forest', name: 'Forest', ramp: [12, 13, 15] },
  { id: 'cream', name: 'Cream', ramp: [8, 10, 11] },
  { id: 'stone', name: 'Stone', ramp: [24, 25, 27] },
];

export const DEFAULT_APPEARANCE = {
  name: 'Player',
  gender: 'male',
  skin: 'tan',
  hairStyle: 'short',
  hairColor: 'brown',
  top: 'sleeveless',
  topColor: 'red',
  bottom: 'pants',
  bottomColor: 'blue',
};

const byId = (list) => new Map(list.map((entry) => [entry.id, entry]));

export const GENDER_BY_ID = byId(GENDERS);
export const SKIN_BY_ID = byId(SKIN_TONES);
export const HAIR_STYLE_BY_ID = byId(HAIR_STYLES);
export const HAIR_COLOR_BY_ID = byId(HAIR_COLORS);
export const TOP_BY_ID = byId(TOPS);
export const BOTTOM_BY_ID = byId(BOTTOMS);
export const CLOTH_COLOR_BY_ID = byId(CLOTH_COLORS);

export function remapTable(author, ramp) {
  return { [author[0]]: ramp[0], [author[1]]: ramp[1], [author[2]]: ramp[2] };
}

export function optionSwatch(entry) {
  const mid = entry.ramp?.[1];
  return mid ?? 1;
}
